import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '~/shadcn/ui/dialog'
import { useUser } from '~/@biz_account/useUser/useUser'
import { OnboardingSteps } from '~/@pages_onboarding/_onboarding-stepper/OnboardingSteps'
import {
  getActiveStepIndex,
  getExpiredIdentityActiveStepIndex,
  getSteps,
  STEPS_EXPIRED_ANALYSIS,
  STEPS_EXPIRED_IDENTITY,
} from '~/@pages_onboarding/_onboarding-stepper/steps'
import { useOnboarding } from '~/@pages_onboarding/useOnboarding'
import { useNavigate } from 'react-router-dom'
import { Button } from '~/shadcn/ui/button/button'

type Props = {
  disabled?: boolean
  className?: string
  onInvest(): void
}

export function InvestButton({ disabled, className, onInvest }: Props) {
  const navigate = useNavigate()
  const { user } = useUser()
  const { isUserApproved } = useOnboarding(user ?? null)
  const activeStep = getActiveStepIndex(user ?? null)
  const stepsRegistration = getSteps(user ?? null)
  const [isModalOpen, setModalOpen] = useState(false)

  const hasExpiredInvestorAnalysis = user?.hasExpiredInvestorAnalysis
  const hasExpiredIdentityDocument = user?.hasExpiredIdentityDocument

  function handleClick() {
    if (!isUserApproved) {
      setModalOpen(true)
      return
    }
    onInvest()
  }

  const content = hasExpiredIdentityDocument
    ? {
        title: 'Platnosť vášho dokladu totožnosti vypršala',
        bodyText:
          'Pre pokračovanie v investovaní je potrebné nahrať platný doklad totožnosti. Kým tak neurobíte, nebude možné investovať, vkladať ani vyberať prostriedky.',
        buttonText: 'Aktualizovať overenie identity',
        steps: STEPS_EXPIRED_IDENTITY,
        activeStep: getExpiredIdentityActiveStepIndex(user ?? null),
      }
    : hasExpiredInvestorAnalysis
      ? {
          title: 'Platnosť vašich dotazníkov vypršala',
          bodyText:
            'Pre pokračovanie v investovaní je potrebné opätovne vyplniť Investičný dotazník a Simuláciu investičného rizika.',
          buttonText: 'Vyplniť dotazník',
          steps: STEPS_EXPIRED_ANALYSIS,
          activeStep: 0,
        }
      : {
          title: 'Sme radi, že vás zaujala táto investícia.',
          bodyText:
            'Aby ste mohli investovať svoje finančné prostriedky, je potrebné dokončiť registráciu. Zaberie Vám to iba pár minút.',
          buttonText: 'Aktualizovať registráciu',
          steps: stepsRegistration,
          activeStep: activeStep,
        }

  return (
    <>
      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="w-3xl p-10">
          <DialogHeader>
            <DialogTitle title={content.title} className="text-base">
              {content.title}
            </DialogTitle>
            <DialogDescription className="mb-6" title={content.bodyText}>
              {content.bodyText}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col lg:flex-row gap-6">
            <OnboardingSteps
              steps={content.steps}
              activeStep={content.activeStep}
              stepIconClassNameOverride="bg-background text-accent"
            />
            <Button
              variant="accent"
              size="xl"
              onClick={() => navigate('/get-started')}>
              {content.buttonText}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Button
        variant="accent"
        disabled={disabled}
        size="3xl"
        onClick={handleClick}
        className={className}>
        Investovať
      </Button>
    </>
  )
}
